import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import classNames from 'classnames';
import { isEmpty, differenceWith, isEqual } from 'lodash-es';

import { NewBookingsStore, RootStore, AddressesStore } from 'AppClient/stores';
import { GoogleAddressAndFlightData, DnDItem } from 'app-types';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { isFlightItem, getPlaceId } from 'utils/flightsUtils';

import { Button, Icon } from 'components/antd';
import DynamicFields from 'components/DynamicFields/DynamicFields';
import { FlightAddressInput } from 'AppClient/components/FlightInput';
import styles from './styles.module.scss';

interface Props {
	newBookingsStore?: NewBookingsStore;
	addressesStore?: AddressesStore;
}
interface State {
	items: DnDItem[];
}

@inject(({ newBookingsStore, addressesStore }: RootStore) => ({
	newBookingsStore,
	addressesStore,
}))
@observer
export default class LocationsDnD extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		const { addresses } = this.props.addressesStore!;
		this.state = {
			items: this.getInitialItems(),
		};

		if (isEmpty(addresses)) {
			// create initial empty addresses for consistency
			this.setNewAddresses([{ rowInitialNumber: 0 }, { rowInitialNumber: 1 }], true);
		}
	}

	getInitialItems = () => {
		const { addresses } = this.props.addressesStore!;

		return !isEmpty(addresses)
			? Object.keys(addresses)
					.filter(item => addresses[item] !== undefined)
					.map((item, _index) => {
						// restore rowInitialNumber from address key
						const ind = item.split('-')[1];

						return { rowInitialNumber: parseInt(ind) };
					})
			: [{ rowInitialNumber: 0 }, { rowInitialNumber: 1 }];
	};

	getLabel(rowActualIndex: number, currentListLength: number) {
		if (rowActualIndex === 0) {
			return 'PICKUP LOCATION';
		}
		if (rowActualIndex === currentListLength - 1) {
			return 'WHERE ARE YOU GOING?';
		}
		return 'GOING THROUGH';
	}

	reorder(list: any[], startIndex: number, endIndex: number) {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);

		return result;
	}

	setNewAddresses = (items: DnDItem[], ignoreState?: boolean) => {
		const {
			resetAddressPoints,
			addresses,
			addAddressPoint,
			setAddresses,
			airportTypes,
			resetSingleLocation,
		} = this.props.addressesStore!;

		if (items.length < this.state.items.length) {
			// case of removing
			const removedItem = differenceWith(this.state.items, items, isEqual)[0];
			resetSingleLocation(`bookingLocation-${removedItem.rowInitialNumber}`);
		}

		resetAddressPoints();
		let tempAddresses: {
			[label: string]: GoogleAddressAndFlightData | string | undefined;
		} = {};
		items.forEach((item, index) => {
			const currentId = `bookingLocation-${item.rowInitialNumber}`;
			const currentAddress = addresses[currentId] as GoogleAddressAndFlightData;

			if (currentAddress) {
				const addressPlaceId = getPlaceId(
					currentAddress,
					index !== items.length - 1,
					airportTypes[currentId]
				);
				const flightId = isFlightItem(currentAddress) ? currentAddress.id : null;

				addAddressPoint(index, {
					addressPlaceId,
					flightId,
				});
			}

			// reset props in object of addresses
			tempAddresses = {
				...tempAddresses,
				[currentId]: currentAddress,
			};
		});

		setAddresses(tempAddresses);

		if (!ignoreState) {
			this.setState({
				items,
			});
		}
	};

	onDragEnd = (result: DropResult) => {
		// dropped outside the list
		if (!result.destination) {
			return;
		}

		const items = this.reorder(this.state.items, result.source.index, result.destination.index);

		this.setNewAddresses(items);
	};

	updateLocationsAmount = (listRange: number[]) => {
		const items = listRange.map(item => {
			return {
				rowInitialNumber: item,
			};
		});
		this.setNewAddresses(items);
	};

	isDraggingFlight = (isDragging: boolean, currentAddress?: GoogleAddressAndFlightData) => {
		return isDragging && currentAddress && isFlightItem(currentAddress as object);
	};

	private add: any = null;
	private disabled: boolean = false;

	render() {
		const { pickupDateTime } = this.props.newBookingsStore!;

		const { addresses } = this.props.addressesStore!;

		return (
			<div>
				<DragDropContext onDragEnd={this.onDragEnd}>
					<Droppable droppableId={`locationDrop`}>
						{provided => (
							<div {...provided.droppableProps} ref={provided.innerRef}>
								<DynamicFields
									listName={'bookingLocations'}
									initialValues={this.state.items}
									min={2}
									max={5}
									updateRangeCallback={this.updateLocationsAmount}
									render={props => {
										const {
											rowInitialNumber,
											rowActualIndex,
											removeRow,
											currentListLength,
										} = props;

										// rowInitialNumber - some constant value assigned to some particular address form addresses
										// it is removing/adding with removning/adding address, it is saving with address and can be restore
										// from address key

										// rowActualIndex - real index of row

										const currentId = `bookingLocation-${rowInitialNumber}`;
										const currentAddress = addresses[
											currentId
										] as GoogleAddressAndFlightData;

										return (
											<Draggable
												key={`locationDrag-${rowInitialNumber}`}
												draggableId={`locationDrag-${rowInitialNumber}`}
												index={rowActualIndex}
											>
												{(provided, dragSnapshot) => (
													<div
														ref={provided.innerRef}
														{...provided.draggableProps}
														{...provided.dragHandleProps}
														style={{ ...provided.draggableProps.style }}
														className={classNames(styles.locationRow, {
															[styles.locationRowDragging]:
																dragSnapshot.isDragging,
															draggingFlight: this.isDraggingFlight(
																dragSnapshot.isDragging,
																currentAddress
															),
														})}
													>
														<div
															className={styles.flightAddressWrapper}
														>
															<FlightAddressInput
																defaultValue={currentAddress}
																isPickup={
																	rowActualIndex !==
																	currentListLength - 1
																}
																index={rowActualIndex}
																pickupDateTime={pickupDateTime}
																label={this.getLabel(
																	rowActualIndex,
																	currentListLength
																)}
																searchLabel={currentId}
																isDragging={dragSnapshot.isDragging}
															/>
															{currentListLength > 2 && (
																<div className="removeButtonCol">
																	<Button
																		icon="remove"
																		size="large"
																		onClick={() =>
																			removeRow(
																				rowInitialNumber
																			)
																		}
																	/>
																</div>
															)}
															<Icon type="reorder" />
														</div>
													</div>
												)}
											</Draggable>
										);
									}}
									renderAddButton={(add, disabled) => {
										this.add = add;
										if (this.disabled !== disabled) {
											this.disabled = disabled;
											this.forceUpdate();
										}
										return null;
									}}
								/>
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
				<div className={styles.addLocation}>
					<Button
						onClick={() => {
							if (this.add) {
								this.add();
							}
						}}
						type="dashed"
						disabled={this.disabled}
					>
						+ Add location
					</Button>
				</div>
			</div>
		);
	}
}
